/*
 * The one combobox behind "@", "#" and ":" (spec 12 §3.5): the textarea keeps focus and carries
 * `aria-activedescendant`, the list is a `listbox` of at most eight options (or the source's own
 * `limit`), Arrow keys move over the options that can be chosen, Enter and Tab insert, Escape closes
 * without inserting and stays closed until the trigger changes. Queries are asynchronous and the last
 * one wins; a slow answer to an older term is dropped.
 *
 * "@" and "#" are a surface's own — they read its members and its conversations — while ":" is the
 * composer's, because emoji depend on nothing a surface knows. `withEmojiSource` is therefore
 * applied here rather than by every caller, and a surface that brings its own ":" source keeps it.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { withEmojiSource } from "../../emoji/source.ts"
import type { AutocompleteItem, AutocompleteSource } from "../types.ts"
import { findTrigger, MAX_ITEMS, type ActiveTrigger } from "./trigger.ts"

export interface AutocompleteOptions {
	sources: readonly AutocompleteSource[]
	value: string
	/** The caret, or null while the box is not focused. */
	caret: number | null
	/**
	 * Replaces `value`'s [start, end) with `insert`. The combobox names the range rather than the
	 * new text, because a formatted surface has no string to splice: it maps the two offsets to
	 * document positions (F-2 §11.1). The textarea builds its `Edit` from them.
	 */
	onReplace(range: { start: number; end: number }, insert: string): void
	/**
	 * A formatted surface takes a source's `insertRich` where it has one — the emoji character
	 * instead of its shortcode, say — because what it shows is the result and not the markup.
	 */
	rich?: boolean
	/**
	 * Whether offset 0 of `value` is the start of the message, which is where an anchored source (a
	 * command palette) may open. The textarea's value is the message; the editor's is one block.
	 * Default true.
	 */
	startsMessage?: boolean
}

/** The two things the combobox reads off a key event; React's and the DOM's both have them. */
export interface KeyEventLike {
	key: string
	preventDefault(): void
}

export interface AutocompleteState {
	open: boolean
	items: AutocompleteItem[]
	/** The source the open list answers for — its footer and width; null while the list is shut. */
	source: AutocompleteSource | null
	activeIndex: number
	listboxId: string
	optionId(index: number): string
	/** Where the trigger character stands in `value` while the list is open, for anchoring it. */
	triggerStart: number | null
	/** True when the key was the combobox's business and the caller must not handle it. */
	onKeyDown(e: KeyEventLike): boolean
	choose(index: number): void
	setActive(index: number): void
}

/**
 * The next item from `from` in `step`'s direction that can be chosen, going round the list; `from`
 * itself when nothing else can.
 */
export function nextChoosable(
	items: readonly AutocompleteItem[],
	from: number,
	step: 1 | -1,
): number {
	for (let n = 1; n <= items.length; n++) {
		const at = (from + step * n + items.length * n) % items.length
		if (items[at]?.disabled !== true) return at
	}
	return from
}

function sameTrigger(a: ActiveTrigger | null, b: ActiveTrigger | null): boolean {
	return a?.source === b?.source && a?.start === b?.start
}

interface QueryResult {
	trigger: ActiveTrigger
	items: AutocompleteItem[]
}

export function useAutocomplete(o: AutocompleteOptions): AutocompleteState {
	const listboxId = useId()
	const [result, setResult] = useState<QueryResult | null>(null)
	const [activeIndex, setActive] = useState(0)
	const [dismissed, setDismissed] = useState<ActiveTrigger | null>(null)
	const sequence = useRef(0)

	const sources = useMemo(() => withEmojiSource(o.sources), [o.sources])
	const trigger = useMemo(
		() => (o.caret === null ? null : findTrigger(o.value, o.caret, sources, o.startsMessage)),
		[o.caret, sources, o.value, o.startsMessage],
	)
	// A trigger that left the text ends its dismissal: typed again at the same place it is a new
	// trigger, and Escape on the last one must not keep the list shut for this one. A box without a
	// caret (it lost the focus) says nothing about the text, so the dismissal outlives a blur. Reset
	// while rendering, React's way to derive state from what changed.
	if (o.caret !== null && trigger === null && dismissed !== null) setDismissed(null)
	const active = trigger !== null && !sameTrigger(trigger, dismissed) ? trigger : null

	// The list is whatever the last query for exactly this trigger and term answered; a result for
	// an older term is simply not the current one, so a slow answer never shows stale items.
	const items = useMemo(
		() =>
			active !== null &&
			result !== null &&
			sameTrigger(result.trigger, active) &&
			result.trigger.term === active.term
				? result.items
				: [],
		[active, result],
	)

	useEffect(() => {
		if (active === null) return
		const mine = ++sequence.current
		void active.source
			.query(active.term)
			.then((found) => {
				if (sequence.current !== mine) return
				const shown = found.slice(0, active.source.limit ?? MAX_ITEMS)
				setResult({ trigger: active, items: shown })
				setActive(
					Math.max(
						0,
						shown.findIndex((item) => item.disabled !== true),
					),
				)
			})
			.catch(() => {
				if (sequence.current === mine) setResult({ trigger: active, items: [] })
			})
	}, [active])

	const open = items.length > 0

	const optionId = useCallback((index: number) => `${listboxId}-${index}`, [listboxId])

	const choose = useCallback(
		(index: number) => {
			const item = items[index]
			if (active === null || item === undefined || item.disabled === true || o.caret === null) {
				return
			}
			let insert =
				(o.rich === true ? active.source.insertRich?.(item) : undefined) ??
				active.source.insert(item)
			if (!/\s$/.test(insert)) insert += " "
			o.onReplace({ start: active.start, end: o.caret }, insert)
		},
		[active, items, o],
	)

	const onKeyDown = useCallback(
		(e: KeyEventLike): boolean => {
			if (!open) return false
			switch (e.key) {
				case "ArrowDown":
					setActive((i) => nextChoosable(items, i, 1))
					break
				case "ArrowUp":
					setActive((i) => nextChoosable(items, i, -1))
					break
				case "Enter":
				case "Tab":
					choose(activeIndex)
					break
				case "Escape":
					setDismissed(active)
					break
				default:
					return false
			}
			e.preventDefault()
			return true
		},
		[open, items, choose, activeIndex, active],
	)

	return {
		open,
		items,
		source: open && active !== null ? active.source : null,
		activeIndex,
		listboxId,
		optionId,
		triggerStart: open && active !== null ? active.start : null,
		onKeyDown,
		choose,
		setActive,
	}
}
